import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const page: ContentPageData = {
  path: '/funktsii/integratsiya-z-crm',
  parent: { name: 'Можливості', path: '/funktsii' },
  crumb: 'Інтеграція з CRM',
  title: 'Інтеграція дзвінків з CRM: вебхуки та API | Anytel',
  description:
    'Заявки, записи в календар і підсумки дзвінків потрапляють у вашу CRM через вебхуки та REST API. Агент може під час розмови брати дані з ваших систем.',
  eyebrow: 'Можливості',
  h1: 'Інтеграція з CRM',
  lead:
    'Розмова закінчилась, а заявка вже у вашій CRM: з ім’ям, телефоном і суттю звернення, а запис розмови чекає в кабінеті. Без ручного переписування і без утрачених деталей.',
  sections: [
    {
      h2: 'Як дані потрапляють у CRM',
      blocks: [
        { h3: 'Вебхуки' },
        'Anytel надсилає подію у вашу CRM у момент, коли щось сталось: нова заявка, зміна її статусу, новий запис у календарі, завершений дзвінок. Ви вказуєте адресу, куди слати, і обираєте події. Більшість CRM і сервісів автоматизації вміють такі події приймати.',
        { h3: 'REST API' },
        'Ваші системи можуть самі звертатися до Anytel: шукати й створювати контакти, читати дзвінки й заявки, перевіряти вільний час у календарі, створювати записи. Доступ видається ключами з обмеженням за розділами: ключ для календаря не побачить записів розмов.',
        { h3: 'Дані під час розмови' },
        'Агент може звернутися до вашої системи прямо в розмові: перевірити статус замовлення, знайти клієнта за телефоном, дізнатися про наявність. Це налаштовується як дія агента або крок сценарію з HTTP-запитом.',
        'Наприклад, в інтернет-магазині агент питає номер замовлення, звертається до вашої облікової системи і називає статус та дату доставки. Клієнт отримує відповідь за хвилину, а менеджер не відривається на «де моє замовлення».',
      ],
    },
    {
      h2: 'Що можна передавати',
      blocks: [
        {
          list: [
            'Нові заявки з усім, що зібрав агент: ім’я, телефон, послуга, бажаний час, коментар.',
            'Записи в календар: хто, до кого, на коли.',
            'Підсумки дзвінків: тривалість, чим закінчився, номер абонента.',
            'Зміни статусів заявок, щоб CRM і кабінет показували одне й те саме.',
          ],
        },
      ],
    },
    {
      h2: 'Шлях однієї заявки',
      blocks: [
        {
          steps: [
            { title: 'Розмова', text: 'Клієнт дзвонить, агент дізнається ім’я, що потрібно і коли зручно.' },
            { title: 'Заявка в кабінеті', text: 'Сценарій створює заявку з усіма зібраними даними і прив’язує до неї запис розмови.' },
            { title: 'Подія у вашу CRM', text: 'Anytel надсилає вебхук про нову заявку. CRM створює ліда або угоду з тими самими полями.' },
            { title: 'Робота в CRM', text: 'Менеджер веде клієнта у звичній CRM. Якщо потрібно, ваша система оновлює статус заявки в Anytel через API, і кабінет показує те саме.' },
          ],
        },
      ],
    },
    {
      h2: 'Якщо ваша CRM не приймає вебхуки',
      blocks: [
        'Є сервіси автоматизації на кшталт Make, n8n чи Zapier: вони приймають вебхук від Anytel і передають дані майже в будь-яку CRM, таблицю чи месенджер. Якщо не знаєте, з чого почати, напишіть нам, підкажемо найпростіший шлях для вашої системи.',
        'Заявки також приходять [у Telegram](/funktsii/zayavky-v-telegram) і на пошту. Часто цього вистачає, щоб почати, а CRM підключити згодом.',
      ],
    },
  ],
  after: [
    {
      h2: 'Як підключити',
      blocks: [
        {
          steps: [
            { title: 'Вебхук', text: 'У кабінеті в розділі «Вебхуки» додайте адресу вашої CRM чи сервісу автоматизації і оберіть події.' },
            { title: 'Ключ API', text: 'Якщо ваша система має сама звертатися до Anytel, створіть ключ у розділі «API-доступ» і обмежте його потрібними розділами.' },
            { title: 'Перевірка', text: 'Зробіть тестовий дзвінок з браузера і переконайтеся, що заявка з’явилась у CRM.' },
          ],
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Які CRM підтримуються?',
      a: 'Будь-яка, що приймає вебхуки або має API. Для систем без них підходять сервіси автоматизації: Make, n8n, Zapier.',
    },
    {
      q: 'Чи можна шукати клієнта в CRM на початку дзвінка?',
      a: 'Так, HTTP-запитом у сценарії: знайти клієнта за номером і далі вести дзвінок з урахуванням відповіді. Наприклад, постійних клієнтів одразу переводити на їхнього менеджера.',
    },
    {
      q: 'Чи можна перенести клієнтів з CRM в Anytel?',
      a: 'Так, через API контакти створюються з вашої бази. Тоді вже з першого дзвінка кабінет показує історію клієнта, а не лише номер.',
    },
    {
      q: 'Чи безпечно давати доступ до API?',
      a: 'Ключ має доступ лише до розділів, які ви обрали, і його можна відкликати в кабінеті будь-коли.',
    },
    {
      q: 'Чи потрібен програміст?',
      a: 'Для вебхука в сервіс автоматизації ні: це налаштовується в інтерфейсі. Для прямої інтеграції з вашою власною системою знадобиться розробник з її боку.',
    },
  ],
  related: ['/funktsii/zayavky-v-telegram', '/funktsii/zapys-i-transkryptsiya-dzvinkiv', '/dlya/avtoservisu', '/dlya/nerukhomosti'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
